import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { Accordion, AccordionDetails, AccordionSummary, Box, Chip, ChipProps, Divider, Grid, Typography } from '@mui/material';
import { FC, ReactNode } from 'react';
import ReactJson from 'react-json-view';
import { Colors } from 'src/components/utils/Theme';

export interface DetailChip {
  label?: string | null;
  color?: ChipProps['color'];
}

export interface DetailField {
  label: string;
  value?: ReactNode;
}

export interface DetailSection {
  title: string;
  fields: DetailField[];
}

interface Props {
  /** Prominent first line, e.g. an amount. */
  headline?: ReactNode;
  /** Smaller line below the headline, e.g. title/date. */
  subheadline?: ReactNode;
  /** Chips shown at the very top (e.g. type/status). Chips without a label are skipped. */
  chips?: DetailChip[];
  /** Action buttons rendered below the header. */
  actions?: ReactNode;
  /** Grouped, labeled key/value rows. Fields with empty values are skipped. */
  sections: DetailSection[];
  /** Full record, shown in a collapsible "Raw data" accordion so nothing is hidden. */
  raw?: object | null;
}

const hasValue = (value: ReactNode) => value !== undefined && value !== null && value !== '';

/** Renders a record as a clean, readable detail layout (header, grouped rows, raw JSON at the bottom). */
export const DetailView: FC<Props> = ({ headline, subheadline, chips, actions, sections, raw }) => {
  return (
    <Box>
      {chips && chips.some((chip) => chip.label) ? (
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 1.5 }}>
          {chips
            .filter((chip) => chip.label)
            .map((chip, index) => (
              <Chip key={index} size="small" label={chip.label} color={chip.color ?? 'default'} />
            ))}
        </Box>
      ) : null}
      {hasValue(headline) ? (
        <Typography variant="h5" style={{ fontFamily: 'Aestetico-semibold', color: Colors.BLACK_BLUE }}>
          {headline}
        </Typography>
      ) : null}
      {hasValue(subheadline) ? (
        <Typography variant="body2" sx={{ color: Colors.GREY, mt: 0.5 }}>
          {subheadline}
        </Typography>
      ) : null}
      {actions ? <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mt: 2 }}>{actions}</Box> : null}
      {sections.map((section) => {
        const fields = section.fields.filter((field) => hasValue(field.value));
        if (fields.length === 0) {
          return null;
        }
        return (
          <Box key={section.title} sx={{ mt: 3 }}>
            <Typography variant="overline" sx={{ color: Colors.GREY, letterSpacing: 1 }}>
              {section.title}
            </Typography>
            <Divider sx={{ mb: 0.5 }} />
            {fields.map((field) => (
              <Grid container key={field.label} sx={{ py: 0.75 }} alignItems="center">
                <Grid item xs={5}>
                  <Typography variant="body2" sx={{ color: Colors.GREY }}>
                    {field.label}
                  </Typography>
                </Grid>
                <Grid item xs={7}>
                  <Typography variant="body2" component="div" sx={{ wordBreak: 'break-all' }}>
                    {field.value}
                  </Typography>
                </Grid>
              </Grid>
            ))}
          </Box>
        );
      })}
      {raw ? (
        <Accordion disableGutters sx={{ mt: 3 }}>
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Typography variant="body2" style={{ fontFamily: 'Aestetico-semibold' }}>
              Raw data
            </Typography>
          </AccordionSummary>
          <AccordionDetails sx={{ overflowX: 'auto' }}>
            <ReactJson src={raw} name={false} collapsed={1} displayDataTypes={false} />
          </AccordionDetails>
        </Accordion>
      ) : null}
    </Box>
  );
};

/** Formats an ISO date string for detail views; falls back to the raw value if unparsable. */
export const formatDetailDate = (date?: string | null): string | undefined => {
  if (!date) {
    return undefined;
  }
  const parsed = new Date(date);
  return isNaN(parsed.getTime()) ? date : parsed.toLocaleString();
};
